/**
 * Event Producers - Profitability Report (Task 56)
 * Event profitability analysis with revenue and expenses
 */

import { useEffect, useMemo } from 'react';
import { useIndustryProjects } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, Users, Calendar } from 'lucide-react';
import { parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MainLayout } from "@/components/MainLayout";

export default function EventsReportPage() {
  useEffect(() => {
    document.title = 'Relatório de Eventos | Controle Financeiro';
  }, []);

  const { projects, isLoading } = useIndustryProjects({ domain: CompanyType.EVENT_PRODUCERS });

  const eventStats = useMemo(() => {
    if (!projects) return [];

    return projects.map((event) => {
      const budget = event.budget || 0;
      const actualCost = event.actual_cost || 0;
      const profit = budget - actualCost;
      const margin = budget > 0 ? (profit / budget) * 100 : 0;

      return {
        id: event.id,
        name: event.name || 'Sem nome',
        status: event.status,
        eventDate: event.metadata?.eventDate as string,
        venue: event.metadata?.venue as string,
        attendees: event.metadata?.attendees as number,
        budget,
        actualCost,
        profit,
        margin,
      };
    }).sort((a, b) => {
      // Sort by event date (upcoming first, then past)
      if (a.eventDate && b.eventDate) {
        return parseISO(b.eventDate).getTime() - parseISO(a.eventDate).getTime();
      }
      return 0;
    });
  }, [projects]);

  const completedEvents = eventStats.filter((e) => e.status === 'completed');
  const totalRevenue = completedEvents.reduce((sum, e) => sum + e.budget, 0);
  const totalCosts = completedEvents.reduce((sum, e) => sum + e.actualCost, 0);
  const totalProfit = totalRevenue - totalCosts;
  const avgMargin = completedEvents.length > 0
    ? completedEvents.reduce((sum, e) => sum + e.margin, 0) / completedEvents.length
    : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planning: 'Planejamento',
      active: 'Em Andamento',
      completed: 'Concluído',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (status === 'completed') return 'default';
    if (status === 'active') return 'secondary';
    if (status === 'cancelled') return 'destructive';
    return 'outline';
  };

  if (isLoading) {
    return (
<MainLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    
    </MainLayout>);
  }

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Relatório de Eventos</h1>
        <p className="text-muted-foreground">Análise de lucratividade por evento</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Receita Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {completedEvents.length} eventos concluídos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Lucro Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(totalProfit)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Receita menos custos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Margem Média
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgMargin.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Percentual de lucro médio
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total de Eventos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Todos os status
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Evento</CardTitle>
          <CardDescription>Análise financeira individual</CardDescription>
        </CardHeader>
        <CardContent>
          {eventStats.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhum evento cadastrado
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Evento</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead className="text-right">Público</TableHead>
                    <TableHead className="text-right">Receita</TableHead>
                    <TableHead className="text-right">Custos</TableHead>
                    <TableHead className="text-right">Lucro</TableHead>
                    <TableHead className="text-right">Margem</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventStats.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="font-medium">{event.name}</TableCell>
                      <TableCell className="text-sm">{formatDate(event.eventDate)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {event.venue || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {event.attendees || '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(event.budget)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(event.actualCost)}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${event.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(event.profit)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={event.margin >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {event.margin.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(event.status)}>
                          {getStatusLabel(event.status)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  
    </MainLayout>);
}
